/**
 * Invoice Ninja (https://invoiceninja.com).
 *
 * @link https://github.com/invoiceninja/invoiceninja source repository
 *
 * @copyright Copyright (c) 2022. Invoice Ninja LLC (https://invoiceninja.com)
 *
 * @license https://www.elastic.co/licensing/elastic-license
 */

import axios, { AxiosError, AxiosRequestConfig, Method } from 'axios';
import { defaultHeaders } from '$app/common/queries/common/headers';
import { ValidationBag } from '../interfaces/validation-bag';
import { toast } from './toast/toast';
import { $refetch } from '../hooks/useRefetch';
import { checkJsonObject } from '../helpers';
import { clearLocalStorage } from './local-storage';

const client = axios.create();

// Create a client without interceptors
const noInterceptClient = axios.create();

client.interceptors.response.use(
  (response) => {
    const payload = checkJsonObject(response.config.data);
    const requestMethod = response.config.method;

    if (response.config?.headers?.['X-Api-Password'] !== undefined) {
      window.dispatchEvent(new CustomEvent('reset.password.required'));
    }

    if (
      requestMethod === 'put' ||
      (requestMethod === 'post' && payload?.action === 'delete') ||
      requestMethod === 'delete'
    ) {
      $refetch(['activities']);
    }

    return response;
  },
  (error: AxiosError<ValidationBag>) => {
    const url = error.response?.config.url;

    if (url?.endsWith('/api/v1/login') && error.response?.status === 401) {
      return Promise.reject(error);
    }

    if (
      url?.includes('einvoice/peppol/setup') &&
      error.response?.status === 401
    ) {
      return Promise.reject(error);
    }

    if (
      url?.includes('einvoice') &&
      (error.response?.status === 401 ||
        error.response?.status === 403 ||
        error.response?.status === 404)
    ) {
      console.error(error);
      return;
    }

    if (
      url?.endsWith('/api/v1/einvoice/token/update') &&
      error.response?.status === 500
    ) {
      return Promise.reject(error);
    }

    if (error.response?.status === 403) {
      toast.error('unauthorized_action');

      return;
    }

    if (error.response?.status === 429 || error.response?.status === 401) {
      window.location.reload();
      clearLocalStorage();
    }

    if (error.response?.status === 404) {
      toast.error('not_found');
      return;
    }

    if (
      error.response?.status &&
      error.response.status !== 412 &&
      error.response.status !== 422 &&
      error.response.status > 399 &&
      error.response.status < 500
    ) {
      toast.error(error.response?.data.message || 'error_title');
    }

    if (error.response?.status && error.response.status === 500) {
      toast.error('error_title');
    }

    if (error.response?.status === 409) {
      toast.processing();
    }

    if (error.response?.status === 422) {
      window.dispatchEvent(
        new CustomEvent('display.error.toaster', {
          detail: {
            error,
          },
        })
      );
    }

    console.error(error);

    return Promise.reject(error);
  }
);

export function request(
  method: Method,
  url: string,
  data?: any,
  config?: AxiosRequestConfig & { skipIntercept?: boolean }
) {
  const axiosClient = config?.skipIntercept ? noInterceptClient : client;

  return axiosClient({
    method,
    url,
    data,
    ...config,
    headers: { ...defaultHeaders(), ...config?.headers },
    signal: config?.signal,
  });
}
